import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import orderService from '../services/orderService';
import './OrderDetailsPage.css';

const OrderDetailsPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  const fetchOrderDetails = async () => {
    setLoading(true);
    try {
      const response = await orderService.getOrderById(orderId);
      if (response.success) {
        setOrder(response.data.order);
        setAdminNotes(response.data.order.admin_notes || '');
      }
    } catch (error) {
      alert(error.message);
      navigate('/orders');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!window.confirm(`Change order status to ${newStatus}?`)) return;

    try {
      const response = await orderService.updateOrderStatus(orderId, newStatus, adminNotes);
      if (response.success) {
        alert('Order status updated successfully');
        fetchOrderDetails();
      }
    } catch (error) {
      alert(error.message);
    }
  };

  const handlePaymentStatusChange = async (newStatus) => {
    if (!window.confirm(`Change payment status to ${newStatus}?`)) return;

    try {
      const response = await orderService.updatePaymentStatus(orderId, newStatus);
      if (response.success) {
        alert('Payment status updated successfully');
        fetchOrderDetails();
      }
    } catch (error) {
      alert(error.message);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#FF9800',
      confirmed: '#2196F3',
      processing: '#9C27B0',
      shipped: '#00BCD4',
      delivered: '#4CAF50',
      cancelled: '#F44336'
    };
    return colors[status] || '#666';
  };

  if (loading) {
    return <div className="loading">Loading order details...</div>;
  }

  if (!order) {
    return <div className="error">Order not found</div>;
  }

  return (
    <div className="order-details-page">
      <div className="page-header">
        <button onClick={() => navigate('/orders')} className="back-btn">
          ← Back to Orders
        </button>
        <h1>Order Details</h1>
      </div>

      <div className="details-grid">
        {/* Order Info */}
        <div className="details-card">
          <h2>Order Information</h2>
          <div className="info-row">
            <span className="label">Order Number:</span>
            <span className="value">{order.order_number}</span>
          </div>
          <div className="info-row">
            <span className="label">Order Date:</span>
            <span className="value">
              {new Date(order.created_at).toLocaleString('en-IN')}
            </span>
          </div>
          <div className="info-row">
            <span className="label">Status:</span>
            <select
              value={order.status}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="status-select"
              style={{ backgroundColor: getStatusColor(order.status) }}
            >
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="processing">Processing</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div className="info-row">
            <span className="label">Payment Status:</span>
            <select
              value={order.payment_status}
              onChange={(e) => handlePaymentStatusChange(e.target.value)}
              className="payment-select"
            >
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </select>
          </div>
          <div className="info-row">
            <span className="label">Payment Method:</span>
            <span className="value">{order.payment_method?.toUpperCase() || 'COD'}</span>
          </div>
        </div>

        {/* Customer Info */}
        <div className="details-card">
          <h2>Customer Information</h2>
          <div className="info-row">
            <span className="label">Business Name:</span>
            <span className="value">{order.user?.business_name}</span>
          </div>
          <div className="info-row">
            <span className="label">Contact Person:</span>
            <span className="value">{order.user?.contact_person}</span>
          </div>
          <div className="info-row">
            <span className="label">Email:</span>
            <span className="value">{order.user?.email}</span>
          </div>
          <div className="info-row">
            <span className="label">Phone:</span>
            <span className="value">{order.user?.phone}</span>
          </div>
        </div>

        {/* Shipping Address */}
        <div className="details-card">
          <h2>Shipping Address</h2>
          <p><strong>{order.shipping_name}</strong></p>
          <p>{order.shipping_address}</p>
          <p>{order.shipping_city}, {order.shipping_state} - {order.shipping_pincode}</p>
          <p>Phone: {order.shipping_phone}</p>
        </div>

        {/* Billing Address */}
        <div className="details-card">
          <h2>Billing Address</h2>
          <p><strong>{order.billing_name}</strong></p>
          <p>{order.billing_address}</p>
          <p>{order.billing_city}, {order.billing_state} - {order.billing_pincode}</p>
          <p>Phone: {order.billing_phone}</p>
          <p>GST: {order.gst_number}</p>
        </div>
      </div>

      {/* Order Items */}
      <div className="details-card full-width">
        <h2>Order Items</h2>
        <table className="items-table">
          <thead>
            <tr>
              <th>Product</th>
              <th>SKU</th>
              <th>Quantity</th>
              <th>Unit Price</th>
              <th>Discount</th>
              <th>Tax</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {order.items?.map((item, index) => (
              <tr key={index}>
                <td>{item.product_name}</td>
                <td>{item.product_sku}</td>
                <td>{item.quantity}</td>
                <td>₹{parseFloat(item.unit_price).toFixed(2)}</td>
                <td>₹{parseFloat(item.discount_amount).toFixed(2)}</td>
                <td>₹{parseFloat(item.tax_amount).toFixed(2)}</td>
                <td><strong>₹{parseFloat(item.total_price).toFixed(2)}</strong></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Price Summary */}
      <div className="details-card price-summary">
        <h2>Price Summary</h2>
        <div className="price-row">
          <span>Subtotal:</span>
          <span>₹{parseFloat(order.subtotal).toFixed(2)}</span>
        </div>
        <div className="price-row discount">
          <span>Discount:</span>
          <span>-₹{parseFloat(order.discount_amount).toFixed(2)}</span>
        </div>
        <div className="price-row">
          <span>GST (18%):</span>
          <span>₹{parseFloat(order.tax_amount).toFixed(2)}</span>
        </div>
        <div className="price-row">
          <span>Shipping:</span>
          <span>₹{parseFloat(order.shipping_charges).toFixed(2)}</span>
        </div>
        <div className="price-row total">
          <span>Total Amount:</span>
          <span>₹{parseFloat(order.total_amount).toFixed(2)}</span>
        </div>
      </div>

      {/* Notes */}
      {order.notes && (
        <div className="details-card full-width">
          <h2>Customer Notes</h2>
          <p>{order.notes}</p>
        </div>
      )}

      {/* Admin Notes */}
      <div className="details-card full-width">
        <h2>Admin Notes</h2>
        <textarea
          value={adminNotes}
          onChange={(e) => setAdminNotes(e.target.value)}
          placeholder="Add internal notes..."
          rows="4"
          className="admin-notes-input"
        />
        <button
          onClick={() => handleStatusChange(order.status)}
          className="save-notes-btn"
        >
          Save Notes
        </button>
      </div>
    </div>
  );
};

export default OrderDetailsPage;
